/**
 * Starter SegmentedControl — wraps Mantine's `<SegmentedControl>` as an
 * iOS-style toggle: a neutral light-grey track with the active segment
 * lifted as a white pill (`--bg-surface` + `--shadow-1`, `--fg-primary`
 * text). Mantine's vertical item separators are off by default
 * (`withItemsBorders={false}`) for the clean, line-free look.
 *
 * Distinct from `<Tabs variant="pill">`, whose button-style tabs are
 * free-standing (no enclosing track) and switch a tab PANEL. Reach for
 * SegmentedControl as the workhorse single-select form toggle (an enclosing
 * track with a sliding white pill, e.g. "Last 7 days / 30 days / 90 days"),
 * and for Tabs when you're switching between content panels.
 */
import { forwardRef } from 'react';
import {
  SegmentedControl as MantineSegmentedControl,
  type SegmentedControlProps as MantineSegmentedControlProps,
} from '@mantine/core';
import styles from './SegmentedControl.module.css';

export interface SegmentedControlProps extends MantineSegmentedControlProps {}

export const SegmentedControl = forwardRef<HTMLDivElement, SegmentedControlProps>(
  function SegmentedControl({ classNames, withItemsBorders = false, ...rest }, ref) {
    const existing = (classNames ?? {}) as {
      root?: string;
      label?: string;
      control?: string;
      indicator?: string;
      input?: string;
    };
    const mergedClassNames = {
      ...existing,
      root: [styles.root, existing.root].filter(Boolean).join(' '),
      label: [styles.label, existing.label].filter(Boolean).join(' '),
      control: [styles.control, existing.control].filter(Boolean).join(' '),
      indicator: [styles.indicator, existing.indicator].filter(Boolean).join(' '),
      // The hidden radio is the real focus target; expose it so the module
      // can drive the keyboard focus ring via .root:has(.input:focus-visible).
      input: [styles.input, existing.input].filter(Boolean).join(' '),
    };

    return (
      <MantineSegmentedControl
        ref={ref}
        withItemsBorders={withItemsBorders}
        classNames={mergedClassNames}
        {...rest}
      />
    );
  },
);
